import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormField, FormSection, Panel } from "@/components/FormField";
import { ListEditor, TagEditor } from "@/components/admin/ListEditor";
import { useBusiness } from "@/context/BusinessContext";
import { api, formatApiError, formatUSD } from "@/lib/api";
import { ui } from "@/lib/ui";

export const BusinessProfileEditor = () => {
  const { profile, setProfile } = useBusiness();
  const [form, setForm] = useState(profile);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const clean = (arr) => arr.map((s) => s.trim()).filter(Boolean);
      const { base_charge, ...body } = form;
      const { data } = await api.put("/business", {
        ...body,
        per_mile_rate: Number(form.per_mile_rate),
        service_areas: clean(form.service_areas),
        transport_options: clean(form.transport_options),
      });
      setProfile(data);
      setForm(data);
      toast.success("Business profile saved", { description: "The public page is updated." });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const text = (k, label, props = {}) => (
    <FormField id={`bp-${k}`} label={label}>
      <Input id={`bp-${k}`} value={form[k]} onChange={set(k)} className={`h-11 ${ui.input}`} data-testid={`profile-${k.replace(/_/g, "-")}-input`} {...props} />
    </FormField>
  );

  return (
    <form onSubmit={save} className="space-y-6" data-testid="business-profile-form">
      <Panel className="p-5 sm:p-7 space-y-8">
        <FormSection step="01" title="Identity & contact">
          <div className="grid sm:grid-cols-2 gap-4">
            {text("company_name", "Company name", { required: true })}
            {text("phone", "Contact number")}
            {text("email", "Contact email", { type: "email" })}
            {text("hq_address", "Headquarters location")}
          </div>
          {text("tagline", "Tagline")}
          <FormField id="bp-about" label="About">
            <Textarea id="bp-about" rows={3} value={form.about} onChange={set("about")} className={ui.input} data-testid="profile-about-input" />
          </FormField>
        </FormSection>
        <FormSection step="02" title="Pricing">
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <FormField id="bp-base" label="Base service charge (fixed)">
              <Input id="bp-base" disabled value={formatUSD(form.base_charge)} className={`h-11 ${ui.input} opacity-70`} data-testid="profile-base-charge-display" />
            </FormField>
            {text("per_mile_rate", "Per-mile rate (USD)", { type: "number", min: "0", step: "0.01", required: true })}
          </div>
        </FormSection>
        <FormSection step="03" title="What we do">
          <ListEditor label="Services" testId="profile-services" items={form.services} onChange={setVal("services")}
            blank={{ title: "", description: "" }}
            fields={[{ key: "title", placeholder: "Service name" }, { key: "description", placeholder: "Short description", multiline: true }]} />
          <TagEditor label="Service areas" items={form.service_areas} onChange={setVal("service_areas")} testId="profile-service-areas-input" />
          <TagEditor label="Transport options" items={form.transport_options} onChange={setVal("transport_options")} testId="profile-transport-options-input" />
        </FormSection>
        <FormSection step="04" title="Leadership">
          <ListEditor label="Owners / leaders" testId="profile-owners" items={form.owners} onChange={setVal("owners")}
            blank={{ name: "", title: "", bio: "" }}
            fields={[{ key: "name", placeholder: "Full name" }, { key: "title", placeholder: "Title" }, { key: "bio", placeholder: "Short bio", multiline: true }]} />
        </FormSection>
      </Panel>
      <Button type="submit" disabled={busy} className={`${ui.btnPrimary} h-12 px-8`} data-testid="save-business-profile-button">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save profile
      </Button>
    </form>
  );
};
